import React, { useState, useEffect } from 'react';
import {
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Checkbox,
  ListItemText,
  Button,
  OutlinedInput,
  Box,
  Card,
  CardContent,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TableSortLabel,
  Paper,
  CircularProgress,
  Alert,
  Chip,
  ListSubheader,
  useMediaQuery,
  useTheme
} from '@mui/material';
import { apiRequest } from '../utils/api';
import { usePrivacyMask } from '../contexts/PrivacyMaskContext';

const AccountSummary = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [data, setData] = useState({ accounts: [], total: {} });
  const [allAccounts, setAllAccounts] = useState([]);
  const [selectedAccount, setSelectedAccount] = useState(['all']);
  const [draftAccount, setDraftAccount] = useState(['all']);
  const [orderBy, setOrderBy] = useState('name');
  const [order, setOrder] = useState('asc');
  const [refreshing, setRefreshing] = useState(false);

  // Grouped Accounts for dropdown
  const taxableAccounts = allAccounts.filter(a => a.accountType === 'taxable');
  const nonTaxableAccounts = allAccounts.filter(a => a.accountType === 'non-taxable');

  const handleAccountCategorySelect = (category) => {
    const categoryAccounts = category === 'taxable' ? taxableAccounts : nonTaxableAccounts;
    const categoryIds = categoryAccounts.map(a => a.id);
    
    // Check if all accounts in this category are already selected
    const allSelected = categoryIds.every(id => draftAccount.includes(id));
    
    let newDraft;
    if (allSelected) {
      // Unselect all in category
      newDraft = draftAccount.filter(id => !categoryIds.includes(id));
    } else {
      // Select all in category
      newDraft = Array.from(new Set([...draftAccount, ...categoryIds])).filter(id => id !== 'all');
    }
    
    if (newDraft.length === 0) newDraft = ['all'];
    setDraftAccount(newDraft);
  };

  useEffect(() => {
    fetchAccountsList();
  }, []);

  useEffect(() => {
    fetchSummary();
  }, [selectedAccount]);

  const fetchAccountsList = async () => {
      try {
          const response = await apiRequest('/api/accounts?include_inactive=true');
          if (response && response.data) {
              setAllAccounts(response.data);
          }
      } catch (err) {
          console.error('Failed to fetch accounts list:', err);
      }
  };

  // Loads the latest saved balance snapshot per account — no live broker
  // calls, so the page renders instantly. Live refresh is manual via the
  // Refresh button (refreshBalances).
  const fetchSummary = async () => {
    try {
      setLoading(true);
      let url = '/api/accounts/summary?source=db';
      if (!selectedAccount.includes('all') && selectedAccount.length > 0) {
          url += `&accountIds=${selectedAccount.join(',')}`;
      }
      const response = await apiRequest(url);
      if (!response || !response.data) return;
      setData(response.data);
    } catch (err) {
      console.error('Failed to fetch account summary:', err);
      setError('Failed to load account summary data.');
    } finally {
      setLoading(false);
    }
  };

  // Explicit live refresh: snapshot current balances from the brokers
  // (server saves them to daily_account_balances), then re-read the DB.
  const refreshBalances = async () => {
    try {
      setRefreshing(true);
      const accountIds = selectedAccount.includes('all') ? null : selectedAccount;
      await apiRequest('/api/accounts/save-daily-balances', {
        method: 'POST',
        body: JSON.stringify({ accountIds }),
      });
      await fetchSummary();
    } catch (err) {
      console.error('Failed to refresh account balances:', err);
      setError('Failed to refresh account balances.');
    } finally {
      setRefreshing(false);
    }
  };

  const handleAccountChange = (event) => {
    const { target: { value } } = event;
    const newValue = typeof value === 'string' ? value.split(',') : value;
    
    if (newValue.includes('all') && !draftAccount.includes('all')) {
      setDraftAccount(['all']);
      return;
    }
    
    if (draftAccount.includes('all') && newValue.length > 1) {
      setDraftAccount(newValue.filter(v => v !== 'all'));
      return;
    }
    
    if (newValue.length === 0) {
      setDraftAccount(['all']);
      return;
    }
    
    setDraftAccount(newValue);
  };

  const applyAccountSelection = () => {
    setSelectedAccount(draftAccount);
  };

  const { privacyMask } = usePrivacyMask();

  const formatCurrency = (value) => {
    if (privacyMask) return '••••••';
    if (value === undefined || value === null) return '-';
    const n = Number(value);
    if (!Number.isFinite(n)) return '$0.00';
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD'
    }).format(n);
  };

  // Matches the Dashboard stat card style: compact CardContent, caption
  // label, bold h6 value.
  const SummaryCard = ({ title, value, color = 'text.primary' }) => (
    <Card sx={{ height: '100%' }}>
      <CardContent sx={{ p: isMobile ? 1 : 1.5, '&:last-child': { pb: isMobile ? 1 : 1.5 } }}>
        <Typography color="textSecondary" variant="caption" gutterBottom display="block" sx={{ fontSize: isMobile ? '0.65rem' : undefined }}>
          {title}
        </Typography>
        <Typography variant={isMobile ? 'body2' : 'h6'} component="div" sx={{ fontWeight: 'bold', color }}>
          {formatCurrency(value)}
        </Typography>
      </CardContent>
    </Card>
  );

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="error">{error}</Alert>
      </Box>
    );
  }

  const { total, accounts } = data;

  // Column sorting (applies within each category group)
  const handleSort = (col) => {
    if (orderBy === col) setOrder(o => (o === 'asc' ? 'desc' : 'asc'));
    else { setOrderBy(col); setOrder('asc'); }
  };

  const CATEGORY_GROUPS = [
    { key: 'investment', label: 'Investments' },
    { key: 'banking', label: 'Banking' },
    { key: 'credit', label: 'Credit Cards' },
    { key: 'loan', label: 'Loans' },
    { key: 'other', label: 'Other' },
  ];

  // Server stores category for Plaid accounts (banking/credit/investment/loan).
  // Everything else is an investment/brokerage account; unclassified Plaid rows
  // (not yet backfilled) fall back to a name check for credit cards.
  const categoryOf = (account) => {
    const cat = String(account.category || '').toLowerCase();
    if (['investment', 'banking', 'credit', 'loan', 'other'].includes(cat)) return cat;
    const apiType = String(account.apiType || '').toLowerCase();
    if (apiType === 'plaid') {
      return /credit\s*card|visa|mastercard|amex|discover/i.test(account.name || '') ? 'credit' : 'banking';
    }
    return 'investment';
  };

  const sortRows = (rows) => [...rows].sort((a, b) => {
    const va = a[orderBy];
    const vb = b[orderBy];
    let cmp;
    if (orderBy === 'name' || orderBy === 'apiType') {
      cmp = String(va || '').localeCompare(String(vb || ''));
    } else {
      const x = Number(va);
      const y = Number(vb);
      cmp = (isNaN(x) ? Infinity : x) - (isNaN(y) ? Infinity : y);
    }
    return order === 'asc' ? cmp : -cmp;
  });

  // One box per category for taxable accounts — Investments, Banking,
  // Credit Cards, etc. Non-taxable accounts get their own Retirement box
  // right after Investments regardless of category.
  const taxableRows = accounts.filter(a => (a.accountType || 'taxable') === 'taxable');
  const retirementRows = sortRows(accounts.filter(a => a.accountType === 'non-taxable'));
  const groupedAccounts = CATEGORY_GROUPS
    .map(g => ({
      ...g,
      rows: sortRows(taxableRows.filter(a => categoryOf(a) === g.key)),
    }))
    .filter(g => g.rows.length > 0);
  if (retirementRows.length > 0) {
    const invIdx = groupedAccounts.findIndex(g => g.key === 'investment');
    groupedAccounts.splice(invIdx >= 0 ? invIdx + 1 : 0, 0, {
      key: 'retirement', label: 'Retirement', rows: retirementRows,
    });
  }

  // Most recent snapshot date across the displayed accounts
  const latestAsOf = (data.accounts || [])
    .map(a => a.asOf)
    .filter(Boolean)
    .sort()
    .pop() || null;

  // P/L columns only appear when the server returned data for at least one
  // account (realized = closed-trade net_pl totals, unrealized = latest
  // position_daily_pl snapshot).
  const showRealized = accounts.some(a => a.realizedPL !== null && a.realizedPL !== undefined);
  const showUnrealized = accounts.some(a => a.unrealizedPL !== null && a.unrealizedPL !== undefined);
  const plCols = (showRealized ? 1 : 0) + (showUnrealized ? 1 : 0);

  const COLUMNS = [
    { id: 'name', label: 'Account Name', width: plCols ? '26%' : '36%' },
    { id: 'apiType', label: 'Type', width: plCols ? '10%' : '16%' },
    { id: 'equity', label: 'Equity', align: 'right', width: plCols ? '12%' : '16%' },
    { id: 'buyingPower', label: 'Buying Power', align: 'right', width: plCols ? '12%' : '16%' },
    { id: 'cash', label: 'Cash', align: 'right', width: plCols ? '12%' : '16%' },
    ...(showRealized ? [{ id: 'realizedPL', label: 'Realized P/L', align: 'right', width: '14%' }] : []),
    ...(showUnrealized ? [{ id: 'unrealizedPL', label: 'Unrealized P/L', align: 'right', width: '14%' }] : []),
  ];

  const TOTAL_COLS = COLUMNS.filter(c => c.align === 'right').map(c => c.id);
  const sumCol = (rows, col) => rows.reduce((s, a) => s + (Number(a[col]) || 0), 0);

  const renderAccountRow = (account) => (
    <TableRow key={account.accountId}>
      <TableCell component="th" scope="row">
        {account.name}
        {account.lastUpdated && (
            <Typography variant="caption" color="text.secondary" display="block">
                Updated {new Date(account.lastUpdated).toLocaleString('en-US', { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' })}
            </Typography>
        )}
        {account.error && (
            <Typography variant="caption" color="error" display="block">
                {account.error}
            </Typography>
        )}
      </TableCell>
      <TableCell>
          <Chip label={account.apiType} size="small" variant="outlined" />
      </TableCell>
      <TableCell align="right">{formatCurrency(account.equity)}</TableCell>
      <TableCell align="right">{formatCurrency(account.buyingPower)}</TableCell>
      <TableCell align="right">{formatCurrency(account.cash)}</TableCell>
      {showRealized && (
        <TableCell align="right" sx={{ color: account.realizedPL == null ? undefined : (account.realizedPL >= 0 ? 'success.main' : 'error.main') }}>
          {account.realizedPL == null ? '-' : formatCurrency(account.realizedPL)}
        </TableCell>
      )}
      {showUnrealized && (
        <TableCell align="right" sx={{ color: account.unrealizedPL == null ? undefined : (account.unrealizedPL >= 0 ? 'success.main' : 'error.main') }}>
          {account.unrealizedPL == null ? '-' : formatCurrency(account.unrealizedPL)}
        </TableCell>
      )}
    </TableRow>
  );

  const renderTableHead = () => (
    <TableHead>
      <TableRow>
        {COLUMNS.map(col => (
          <TableCell key={col.id} align={col.align} width={col.width} sortDirection={orderBy === col.id ? order : false}>
            <TableSortLabel
              active={orderBy === col.id}
              direction={orderBy === col.id ? order : 'asc'}
              onClick={() => handleSort(col.id)}
            >
              {col.label}
            </TableSortLabel>
          </TableCell>
        ))}
      </TableRow>
    </TableHead>
  );

  return (
    <Box sx={{ p: { xs: 1, sm: 3 } }}>

      {/* Aggregate Metrics — matches Dashboard stat card layout */}
      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: isMobile ? 1 : 2, mb: isMobile ? 2 : 4 }}>
        <Box sx={{ flex: isMobile ? '1 1 45%' : '1 1 0', minWidth: isMobile ? '45%' : 0 }}>
          <SummaryCard
            title="Total Equity"
            value={total.equity}
            color="primary.main"
          />
        </Box>
        <Box sx={{ flex: isMobile ? '1 1 45%' : '1 1 0', minWidth: isMobile ? '45%' : 0 }}>
          <SummaryCard
            title="Total Buying Power"
            value={total.buyingPower}
          />
        </Box>
        <Box sx={{ flex: isMobile ? '1 1 45%' : '1 1 0', minWidth: isMobile ? '45%' : 0 }}>
          <SummaryCard
            title="Total Cash"
            value={total.cash}
          />
        </Box>
        <Box sx={{ flex: isMobile ? '1 1 45%' : '1 1 0', minWidth: isMobile ? '45%' : 0 }}>
          <SummaryCard
            title="Long Options Value"
            value={total.longMarketValue}
            color="success.main"
          />
        </Box>
        <Box sx={{ flex: isMobile ? '1 1 45%' : '1 1 0', minWidth: isMobile ? '45%' : 0 }}>
          <SummaryCard
            title="Short Options Value"
            value={total.shortMarketValue}
            color="error.main"
          />
        </Box>
      </Box>

      {/* Individual Accounts Table */}
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 1, mt: isMobile ? 2 : 4, mb: 1 }}>
        <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 1 }}>
          <Typography variant="h5">
            Account Details
          </Typography>
          {latestAsOf && (
            <Typography variant="caption" color="text.secondary">
              as of {latestAsOf}
            </Typography>
          )}
        </Box>
        <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
          <FormControl sx={{ minWidth: 200 }} variant="outlined" size="small">
            <InputLabel>Account</InputLabel>
            <Select
              multiple
              value={draftAccount}
              onChange={handleAccountChange}
              input={<OutlinedInput label="Account" />}
              renderValue={(selected) => {
                if (selected.includes('all')) return 'All Accounts';
                if (selected.length === 1) {
                  const acc = allAccounts.find(a => a.id === selected[0]);
                  return acc ? acc.name : selected[0];
                }
                return `${selected.length} Accounts`;
              }}
            >
              <MenuItem value="all" dense>
                <Checkbox size="small" checked={draftAccount.includes('all')} />
                <ListItemText primary="All Accounts" />
              </MenuItem>

              {taxableAccounts.length > 0 && [
                <ListSubheader
                  key="taxable-header"
                  onMouseDown={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                  }}
                  onClick={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    handleAccountCategorySelect('taxable');
                  }}
                  sx={{
                    cursor: 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    bgcolor: 'rgba(0,0,0,0.05)',
                    '&:hover': { bgcolor: 'rgba(0,0,0,0.1)' },
                    pointerEvents: 'auto'
                  }}
                >
                  <Checkbox
                    size="small"
                    checked={taxableAccounts.length > 0 && taxableAccounts.every(a => draftAccount.includes(a.id))}
                    indeterminate={taxableAccounts.some(a => draftAccount.includes(a.id)) && !taxableAccounts.every(a => draftAccount.includes(a.id))}
                    onMouseDown={(e) => e.stopPropagation()}
                  />
                  Taxable
                </ListSubheader>,
                ...taxableAccounts.map((account) => (
                  <MenuItem key={account.id} value={account.id} dense>
                    <Checkbox size="small" checked={draftAccount.indexOf(account.id) > -1} />
                    <ListItemText primary={account.name} />
                  </MenuItem>
                ))
              ]}

              {nonTaxableAccounts.length > 0 && [
                <ListSubheader
                  key="non-taxable-header"
                  onMouseDown={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                  }}
                  onClick={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    handleAccountCategorySelect('non-taxable');
                  }}
                  sx={{
                    cursor: 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    bgcolor: 'rgba(0,0,0,0.05)',
                    '&:hover': { bgcolor: 'rgba(0,0,0,0.1)' },
                    pointerEvents: 'auto'
                  }}
                >
                  <Checkbox
                    size="small"
                    checked={nonTaxableAccounts.length > 0 && nonTaxableAccounts.every(a => draftAccount.includes(a.id))}
                    indeterminate={nonTaxableAccounts.some(a => draftAccount.includes(a.id)) && !nonTaxableAccounts.every(a => draftAccount.includes(a.id))}
                    onMouseDown={(e) => e.stopPropagation()}
                  />
                  Retirement
                </ListSubheader>,
                ...nonTaxableAccounts.map((account) => (
                  <MenuItem key={account.id} value={account.id} dense>
                    <Checkbox size="small" checked={draftAccount.indexOf(account.id) > -1} />
                    <ListItemText primary={account.name} />
                  </MenuItem>
                ))
              ]}

              {/* Show uncategorized if any (fallback) */}
              {allAccounts.filter(a => !a.accountType).map((account) => (
                <MenuItem key={account.id} value={account.id} dense>
                  <Checkbox size="small" checked={draftAccount.indexOf(account.id) > -1} />
                  <ListItemText primary={account.name} />
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <Button
            onClick={applyAccountSelection}
            variant="contained"
            size="small"
            disabled={JSON.stringify(draftAccount.sort()) === JSON.stringify(selectedAccount.sort())}
            sx={{ minWidth: '40px', px: 2 }}
          >
            OK
          </Button>
          <Button
            onClick={refreshBalances}
            variant="outlined"
            size="small"
            disabled={refreshing}
            sx={{ minWidth: '90px', px: 2 }}
          >
            {refreshing ? <CircularProgress size={18} /> : 'Refresh'}
          </Button>
        </Box>
      </Box>
      {/* One box per category, totals on the box header */}
      {groupedAccounts.map((group) => (
        <TableContainer key={group.key} component={Paper} sx={{ mb: 2 }}>
          <Table sx={{ tableLayout: 'fixed' }}>
            {renderTableHead()}
            <TableBody>
              <TableRow sx={{ bgcolor: 'action.hover' }}>
                <TableCell sx={{ fontWeight: 700, fontSize: '0.85rem', py: 0.75 }}>
                  {group.label}
                </TableCell>
                <TableCell sx={{ py: 0.75 }} />
                {TOTAL_COLS.map(col => (
                  <TableCell key={col} align="right" sx={{ fontWeight: 700, fontSize: '0.85rem', py: 0.75 }}>
                    {formatCurrency(sumCol(group.rows, col))}
                  </TableCell>
                ))}
              </TableRow>
              {group.rows.map(renderAccountRow)}
            </TableBody>
          </Table>
        </TableContainer>
      ))}
    </Box>
  );
};

export default AccountSummary;
